/**
 * IGTV/Reels Import - Modern Form
 *
 * Modern form for manually importing Instagram posts/reels.
 * Features:
 * - File upload with preview
 * - Date/time picker
 * - Sked Link URL input
 * - Caption textarea
 * - Modern Tailwind styling
 * - Proper validation and error handling
 */

import {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Moment from "moment";
import "moment-timezone";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Button } from "components/ui/button";
import api from "api/instagramApi";
import { uploadFileToS3 } from "api/media-library/upload-file-to-s3";
import { getUser } from "selectors/commonSelectors";

const igApi = new api();

interface Post {
  url?: string;
  thumbnail_src?: string;
  thumbnailUrl?: string;
  is_video: boolean;
  date?: Date;
  redirectUrl?: string;
  caption?: string;
}

export interface IgtvReelsImportHandle {
  startImport: () => Promise<boolean>;
  canImport: () => boolean;
  isImporting: () => boolean;
}

interface IgtvReelsImportModernProps {
  accountId: string;
  onImported?: () => void;
  onCanImportChange?: (canImport: boolean) => void;
}

function isValidDate(value?: Date) {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

const IgtvReelsImportModern = forwardRef<
  IgtvReelsImportHandle,
  IgtvReelsImportModernProps
>(({ accountId, onImported = () => {}, onCanImportChange = () => {} }, ref) => {
  const user = useSelector(getUser);
  const timezone = user?.timezone || "UTC";

  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [post, setPost] = useState<Post>({
    is_video: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notify parent when the "can import" state changes
  useEffect(() => {
    onCanImportChange(!!post.url && !isUploading && !isImporting);
  }, [post.url, isUploading, isImporting, onCanImportChange]);

  async function uploadMedia(media: File[]) {
    if (!media || media.length === 0) {
      toast.error("No file selected");
      return;
    }

    setIsUploading(true);
    try {
      const file = media[0];
      const fileUrl = await uploadFileToS3({
        blob: file,
        fileName: file.name,
      });

      const uploadedPost = {
        url: fileUrl,
        thumbnail_src: fileUrl,
        thumbnailUrl: fileUrl,
        is_video: file.type.startsWith("video/"),
      };

      setPost({ ...post, ...uploadedPost });
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      const message = error instanceof Error ? error.message : String(error);
      toast.error(`Failed to upload image: ${message}`);
    } finally {
      setIsUploading(false);
    }
  }

  // Calculate selected date before it's used in useImperativeHandle
  const selectedDate = isValidDate(post.date)
    ? post.date
    : Moment().tz(timezone).toDate();

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    async startImport() {
      if (!post.url) {
        toast.error("Please upload a thumbnail image first.");
        return false;
      }

      if (!isValidDate(selectedDate)) {
        toast.error("Please choose a valid post date and time.");
        return false;
      }

      setIsImporting(true);
      const payload = {
        accountId,
        post: { ...post, date: post.date || selectedDate },
      };
      try {
        await igApi.manuallyImportPost(payload);
        toast.success("Instagram post imported successfully!");
        onImported();
        return true;
      } catch (error) {
        console.error(error);
        const apiErrorMessage =
          typeof error === "object" &&
          error !== null &&
          "data" in error &&
          typeof error.data === "object" &&
          error.data !== null &&
          "error" in error.data
            ? String(error.data.error)
            : null;
        const message =
          error instanceof Error
            ? error.message
            : apiErrorMessage || String(error);
        toast.error(`Error importing post: ${message}`);
        return false;
      } finally {
        setIsImporting(false);
      }
    },
    canImport() {
      return !!post.url && !isUploading && !isImporting;
    },
    isImporting() {
      return isImporting;
    },
  }));

  return (
    <div className="space-y-4">
      {/* Thumbnail Upload */}
      <div className="space-y-1.5">
        <label
          htmlFor="file"
          className="font-gotham text-sm font-medium text-neutral-700"
        >
          Thumbnail image (required)
        </label>
        <p className="font-inter text-xs text-neutral-500">
          Pick the thumbnail / post image for this post (image only - PNG or
          JPG)
        </p>
        <div className="mt-2">
          <input
            ref={fileInputRef}
            type="file"
            id="file"
            onChange={(event) => {
              if (event.target.files) {
                uploadMedia([...event.target.files]);
              }
            }}
            accept="image/*"
            className="sr-only"
            disabled={isUploading}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="border-neutral-200 hover:border-neutral-300"
          >
            {isUploading ? (
              <>
                <i className="fa-regular fa-spinner-third fa-spin mr-2" />
                Uploading...
              </>
            ) : post.thumbnailUrl ? (
              <>
                <i className="fa-regular fa-check mr-2" />
                Change image
              </>
            ) : (
              <>
                <i className="fa-regular fa-cloud-arrow-up mr-2" />
                Choose file
              </>
            )}
          </Button>
          {post.thumbnailUrl && (
            <div className="mt-4">
              <img
                src={post.thumbnailUrl}
                alt="Thumbnail preview"
                className="max-w-[70%] max-h-[300px] rounded-lg border border-neutral-200 mx-auto block"
              />
            </div>
          )}
        </div>
      </div>

      {/* Date and Time Picker */}
      <div className="space-y-1.5">
        <label className="font-gotham text-sm font-medium text-neutral-700">
          Date and time for post (required)
        </label>
        <p className="font-inter text-xs text-neutral-500">
          The time and date this post should show as 'posted' in your account's
          timezone ({timezone})
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-gotham text-xs font-medium text-neutral-700 mb-1.5 block">
              Date
            </label>
            <Input
              type="date"
              value={Moment(selectedDate).format("YYYY-MM-DD")}
              onChange={(e) => {
                if (!e.target.value) {
                  return;
                }

                const newDate = Moment(e.target.value)
                  .hour(Moment(selectedDate).hour())
                  .minute(Moment(selectedDate).minute())
                  .toDate();

                if (!isValidDate(newDate)) {
                  return;
                }

                setPost({ ...post, date: newDate });
              }}
              className="border-neutral-200 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="font-gotham text-xs font-medium text-neutral-700 mb-1.5 block">
              Time
            </label>
            <Input
              type="time"
              value={Moment(selectedDate).format("HH:mm")}
              onChange={(e) => {
                if (!e.target.value) {
                  return;
                }

                const [hours, minutes] = e.target.value.split(":");
                const parsedHours = Number.parseInt(hours, 10);
                const parsedMinutes = Number.parseInt(minutes, 10);

                if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) {
                  return;
                }

                const newDate = Moment(selectedDate)
                  .hour(parsedHours)
                  .minute(parsedMinutes)
                  .toDate();

                if (!isValidDate(newDate)) {
                  return;
                }

                setPost({ ...post, date: newDate });
              }}
              className="border-neutral-200 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Sked Link URL */}
      <div className="space-y-1.5">
        <label
          htmlFor="redirectUrl"
          className="font-gotham text-sm font-medium text-neutral-700"
        >
          Sked Link URL (optional)
        </label>
        <p className="font-inter text-xs text-neutral-500">
          The URL to use for redirecting followers from your Sked Link page (if
          used)
        </p>
        <Input
          id="redirectUrl"
          placeholder="https://mywebsite.com"
          type="url"
          value={post.redirectUrl || ""}
          onChange={(e) => setPost({ ...post, redirectUrl: e.target.value })}
          className="border-neutral-200 focus:border-blue-500"
        />
      </div>

      {/* Caption */}
      <div className="space-y-1.5">
        <label
          htmlFor="caption"
          className="font-gotham text-sm font-medium text-neutral-700"
        >
          Caption (optional)
        </label>
        <p className="font-inter text-xs text-neutral-500">
          Copy in the caption if you want it to display on the archives page
        </p>
        <Textarea
          id="caption"
          value={post.caption || ""}
          onChange={(e) => setPost({ ...post, caption: e.target.value })}
          className="min-h-[80px] border-neutral-200 focus:border-blue-500 resize-none"
          placeholder="Add a caption for this post..."
        />
      </div>
    </div>
  );
});

IgtvReelsImportModern.displayName = "IgtvReelsImportModern";

export default IgtvReelsImportModern;
